import { AlertColor, darken, lighten } from "@mui/material";
import { blue, common, cyan, deepPurple, green, grey, orange, red } from "@mui/material/colors";
import { alpha, createTheme, Theme, TypographyVariants } from "@mui/material/styles";

import { SomeRequired, Unionize } from "./types/generic";
import { capitalizeFirstLetter } from "./utils/strings";
import { fontFamily } from "./fontFamily";

export const SPACING = 8;

/**
 * MUI's theme.spacing() function will return the value as a string (e.g. "8px"). Sometimes we want to use
 * the spacing value as a number (e.g. for calculations), so this function returns the value as a number.
 * @param factor The factor by which to multiply the base spacing value.
 * @returns
 */
export const themeSpacing = (factor: number): number => SPACING * factor;

const createAlertVariantClasses = (variant: "compact", theme: Theme): Record<string, any> =>
  (["error", "info", "success", "warning"] as AlertColor[]).reduce(
    (acc, severity) => ({
      ...acc,
      [`&.MuiAlert-${variant}${capitalizeFirstLetter(severity)}`]: {
        color: darken(theme.palette[severity].light, 0.6),
        backgroundColor: lighten(theme.palette[severity].light, 0.9),
        "& .MuiAlert-icon": {
          fontSize: 18,
          color: theme.palette[severity].main,
        },
      },
    }),
    {}
  );

/**
 * Biarri Branding documentation {@link https://docs.google.com/presentation/d/1YrJSbViL-ZEf3MB51raDjRvcEx2J3-k2TSRJ6fQpvsc}
 * Note: As we transition to using a fully implemented Material UI Theme structure vars.scss contains duplicate variables (vars will eventually be removed)
 */
const biarri = {
  primary: {
    carbon: "#839DAD",
    darkGrey: "#525252",
    grey: "#9D9D9D",
    lightGrey: "#E0E0E0", // rgba(0, 0, 0, 0.12) on white
    red: "#E1373B",
    midnight: "#01161F",
  },
  secondary: {
    activeBlue: "#2196f3",
    blue: "#5BB1E2",
    darkBlue: "#1682B0",
    darkCarbon: "#5C7A8C",
    green: "#A0C850",
    grey: "#656565",
    lightGrey: "#707070",
    lightBlue: "#F4F8FA",
    lightCarbon: "#C6DAE3",
    orange: "#FFC800",
    skyBlue: "#E9F4FE",
    stripeBlue: "#0570de",
    yellow: "#FFF000",
    whiteSmoke: "#F5F5F5",
    paleGrey: "#FBFCFE",
    activeGrey: "rgba(0, 0, 0, 0.60)",
    inactiveGrey: "rgba(0, 0, 0, 0.38)",
    darkGrey: "rgba(0, 0, 0, 0.87)",
    disabled: "rgba(0, 0, 0, 0.04)",
    cyan: cyan[500],
  },
} as const;
export type BiarriColors = typeof biarri;

export const GradientColor = ["red", "green", "blue", "orange", "purple"] as const;
export type GradientColor = Unionize<typeof GradientColor>;

/**
 * Typography size variants used across the application.
 */
export const TypographySizeVariants = {
  /** 11px */
  xsmall: "0.69rem",

  /** 12px */
  small: "0.75rem",

  /** 14px */
  normal: "0.875rem",

  /** 16px */
  medium: "1rem",

  /** 20px */
  large: "1.25rem",
} as const;
export type TypographySizeVariants = typeof TypographySizeVariants;

export { fontFamily };

const typography: SomeRequired<TypographyVariants, "size"> = {
  size: TypographySizeVariants,

  fontFamily,

  button: {
    textTransform: "none" as const, // https://github.com/mui/material-ui/issues/16307
  },
  h1: {
    fontSize: "1.75rem",
    fontWeight: 700,
  },

  h2: {
    fontSize: "1.5rem",
    fontWeight: 500,
  },

  h3: {
    fontSize: "1.25rem",
    fontWeight: 500,
  },

  h4: {
    fontSize: "1.5rem",
    fontWeight: 500,
    textTransform: "capitalize" as const, // https://github.com/mui/material-ui/issues/16307
  },

  h5: {
    fontSize: "1.25rem",
    fontWeight: 500,
  },

  h6: {
    fontSize: "1rem",
    fontWeight: 500,
  },

  h7: {
    fontSize: "0.75rem",
    fontWeight: 400,
  },

  body1: {
    fontSize: 14,
  },

  body3: {
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.87)",
  },

  subtle: {
    color: biarri.secondary.grey,
  },

  heading: {
    color: biarri.primary.red,
    fontSize: "0.85rem",
    fontWeight: 500,
  },

  subtitle3: {
    fontSize: 12,
    letterSpacing: "2.5px",
    color: biarri.primary.red,
    textTransform: "uppercase",
  },

  content: {
    color: biarri.primary.darkGrey,
    fontFamily,
    fontSize: 16,
  },

  overline: {
    color: biarri.primary.darkGrey,
    fontFamily,
    fontSize: 14,
    letterSpacing: 1.5,
    textTransform: "uppercase" as const, // https://github.com/mui/material-ui/issues/16307
    fontWeight: 500,
  },
};

export const BorderRadiusVariants = {
  small: "2px",
  medium: "4px",
  large: "8px",
} as const;
export type BorderRadiusVariants = typeof BorderRadiusVariants;

/**
 * Biarri Material-UI Theme definition
 */
// eslint-disable-next-line import/no-mutable-exports
let theme = createTheme({
  spacing: SPACING,
  shape: {
    borderRadius: 4,
  },
  palette: {
    action: {
      active: "rgba(0, 0, 0, 0.4)",
    },
    primary: {
      light: blue[400],
      main: blue[500],
      dark: blue[600],
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#E1373B",
    },
    tertiary: {
      main: deepPurple.A200,
      contrastText: "#FFFFFF",
    },
    background: {
      active: blue[50],
      default: "#F9FAFB",
      paper: "#FFFFFF",
      dialog: "#F9F9F9",
      sidebar: {
        primary: biarri.primary.midnight,
        hover: biarri.secondary.darkCarbon,
      },
      topbar: {
        hover: "rgba(255, 255, 255, 0.04)",
      },
      gridRow: {
        active: "#EBF6FF",
      },
    },
    error: {
      main: biarri.primary.red,
    },
    destructive: {
      main: biarri.primary.red,
      contrastText: "#FFFFFF",
    },
    biarri,
    success: {
      main: "#4CAF50",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#EF6C00",
    },
    subtle: {
      main: "rgba(0 0 0 / 8%)",
      contrastText: biarri.secondary.darkGrey,
    },
    gradient: {
      red: `linear-gradient(180deg, #E1373B 0%, #C8191D 100%)`,
      green: `linear-gradient(180deg, #07DD84 0%, #00D0CC 100%)`,
      blue: `linear-gradient(180deg, #51ABF9 0%, #2196F3 100%)`,
      orange: `linear-gradient(180deg, #FF7B00 0%, #E1373B 100%)`,
      purple: `linear-gradient(180deg, #916DF4 0%, #7C4DFF 100%)`,
    },
  },
  custom: {
    shadows: {
      default: "0px 1px 4px rgba(0, 0, 0, 0.3)",
      inset: "inset 0px 1px 2px rgba(0, 0, 0, 0.3)",
    },
    borderColor: {
      divider: "rgba(0, 0, 0, 0.12)",
      gridRow: {
        current: "rgba(33, 150, 243, 0.3)",
      },
      focused: "rgba(0 0 0 / 25%)",
    },
    code: {
      background: "rgba(0 0 0 / 5%)",
      borderColor: "rgba(0 0 0 / 12%)",
    },
    shape: {
      borderRadius: BorderRadiusVariants,
    },
    action: {
      primaryHover: "rgba(33, 150, 243, 0.04)",
    },
  },
  typography,
});

theme = createTheme(theme, {
  /**
   * Global style for all Mui Components
   */
  components: {
    MuiChip: {
      variants: [
        {
          props: { variant: "subtle", color: "error" },
          style: {
            backgroundColor: alpha(red[500], 0.12),
            color: red[500],
          },
        },
        {
          props: { variant: "subtle", color: "info" },
          style: {
            backgroundColor: alpha(blue[500], 0.12),
            color: blue[500],
          },
        },
        {
          props: { variant: "subtle", color: "success" },
          style: {
            backgroundColor: alpha(green[500], 0.12),
            color: green[700],
          },
        },
        {
          props: { variant: "subtle", color: "warning" },
          style: {
            backgroundColor: alpha(orange[500], 0.12),
            color: orange[700],
          },
        },
        {
          props: { variant: "subtle", size: "small" },
          style: { fontSize: typography.size.small },
        },
      ],
    },
    MuiAlert: {
      variants: [
        {
          props: { variant: "compact" },
          style: {
            fontSize: 12,
            "& .MuiAlertTitle-root": {
              fontSize: 12,
            },
            ...createAlertVariantClasses("compact", theme),
          },
        },
      ],
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 500,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          fontWeight: 500,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: biarri.primary.midnight,
          fontWeight: 500,
          fontSize: "0.875rem",
          minHeight: "0.875rem",
        },
        asterisk: {
          color: biarri.secondary.darkBlue,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          cursor: "pointer",
          "&:hover": {
            color: blue[600],
          },
        },
      },
      defaultProps: {
        underline: "none",
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          "&.Mui-selected, &.Mui-selected:hover": {
            borderRadius: 4,
            backgroundColor: "rgba(0,0,0,0.08)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: 14,
          "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
            borderColor: biarri.secondary.darkBlue,
          },
          "&.Mui-disabled svg": {
            color: "rgba(0, 0, 0, 0.25)",
          },
          "&.Mui-disabled": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
        inputAdornedStart: {
          paddingLeft: 16,
        },
        adornedStart: {
          paddingLeft: 8,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: biarri.secondary.lightBlue,
          "&:hover, &.Mui-focused": {
            backgroundColor: "#E3EDF2",
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          },
        },
        inputSizeSmall: {
          paddingTop: "15px",
          paddingBottom: "15px",
        },
        underline: {
          "&:before": {
            borderColor: `${biarri.secondary.lightCarbon} !important`,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          "&:before": {
            border: "none !important",
          },
          "&:focus": {
            backgroundColor: "none",
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        inset: {
          paddingLeft: 52,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "rgba(0,0,0,0.08)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          height: 52,
          backgroundColor: "#fbfbfb",
        },
        footer: {
          backgroundColor: "#fbfbfb",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.datagrid-rowgroup": {
            backgroundColor: "#F7F7F7",
            "&> td": {
              fontWeight: 500,
              padding: "8px 16px",
            },
          },
          "&.Mui-selected": {
            backgroundColor: blue[50],
            "&$hover:hover": {
              backgroundColor: blue[50],
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: "white",
          borderRadius: BorderRadiusVariants.medium,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${biarri.primary.lightGrey}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          zIndex: 2100,
        },
        tooltip: {
          backgroundColor: "rgba(1, 22, 31, 0.90)",
        },
        arrow: {
          color: "rgba(1, 22, 31, 0.90)",
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".mapboxgl-popup": {
          zIndex: 2,
        },
        ".mapboxgl-popup-content": {
          boxShadow: "0px 1px 4px rgb(0 0 0 / 30%)",
        },
        ".mapboxgl-popup-dragged .mapboxgl-popup-tip": {
          display: "none",
        },
        // MuiDataGrid is still in the Lab and therefore needs to be overrride using
        ".MuiDataGrid-root": {
          border: "none !important",
        },
        ".MuiDataGrid-columnsContainer": {
          backgroundColor: "#fbfbfb",
        },
        ".MuiDataGrid-colCell[role=columnheader]": {
          minHeight: "56px",
        },
        ".MuiDataGrid-columnSeparator": {
          display: "none !important",
        },
        ".customScrollbars::-webkit-scrollbar, .public-DraftEditor-content::-webkit-scrollbar, .flexlayout__tab::-webkit-scrollbar": {
          width: 17,
        },
        ".customScrollbars::-webkit-scrollbar-track, .public-DraftEditor-content::-webkit-scrollbar-track, .flexlayout__tab::-webkit-scrollbar-track":
          {
            background: "#ffffff",
            borderRadius: 4,
          },
        ".customScrollbars.greyBackground::-webkit-scrollbar-track": {
          background: "#f9fafb",
        },
        ".customScrollbars::-webkit-scrollbar-thumb, .public-DraftEditor-content::-webkit-scrollbar-thumb, .flexlayout__tab::-webkit-scrollbar-thumb":
          {
            backgroundColor: "#aeaeae",
            borderRadius: 20,
            border: "5px solid #ffffff",
          },
        ".customScrollbars.greyBackground::-webkit-scrollbar-thumb": {
          border: "5px solid #f9fafb",
        },
        ".darkScrollbars::-webkit-scrollbar, .public-DraftEditor-content::-webkit-scrollbar, .flexlayout__tab::-webkit-scrollbar": {
          width: 14,
        },
        ".darkScrollbars::-webkit-scrollbar-track, .public-DraftEditor-content::-webkit-scrollbar-track, .flexlayout__tab::-webkit-scrollbar-track": {
          background: "#F9F9F9",
          borderRadius: 4,
        },
        ".darkScrollbars::-webkit-scrollbar-thumb, .public-DraftEditor-content::-webkit-scrollbar-thumb, .flexlayout__tab::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 20,
          border: "4px solid #F9F9F9",
        },
        ".ag-body-viewport::-webkit-scrollbar, .ag-body-vertical-scroll-viewport::-webkit-scrollbar": {
          width: 17,
        },
        ".ag-body-viewport::-webkit-scrollbar-track, .ag-body-vertical-scroll-viewport::-webkit-scrollbar-track": {
          background: "#ffffff",
        },
        ".ag-body-viewport::-webkit-scrollbar-thumb, .ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb": {
          backgroundColor: "#aeaeae",
          borderRadius: 20,
          border: "5px solid #ffffff",
        },
        ".ag-body-horizontal-scroll-viewport::-webkit-scrollbar": {
          width: 17,
        },
        ".ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track": {
          background: "#ffffff",
        },
        ".ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb": {
          backgroundColor: "#aeaeae",
          borderRadius: 20,
          border: "5px solid #ffffff",
        },
        ".ReactCollapse--collapse": {
          transition: "height 250ms",
        },
        ".public-DraftEditor-content[contenteditable=true]": {
          fontSize: 12,
          padding: "8px 12px",
          cursor: "text",
          border: "1px solid #c4c4c4",
          borderRadius: 4,
          "&:focus": {
            borderColor: blue[500],
          },
        },
        ".public-DraftEditorPlaceholder-inner": {
          padding: "8px 13px",
          border: "1px solid transparent",
        },
        ".public-DraftEditorPlaceholder-root": {
          fontSize: 12,
        },
        ".flexlayout__tab_button_leading svg": {
          width: 16,
          height: 16,
        },
        ".flexlayout__drag_rect": {
          backgroundColor: biarri.primary.red,
          color: common.white,
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.3)",
          border: "none",
          fontWeight: 500,
          opacity: 1,
          padding: "8px",
        },
        ".flexlayout__edge_rect": {
          backgroundColor: biarri.primary.red,
        },
        // With the upgrade to mapbox-gl 2.8.2 the <canvas> was being
        // set with fractional a height value (e.g. height=1200.25px)
        // due to the flexlayout-react widget which results in a blurry canvas,
        // therefore to avoid this we correct the 0.25 height
        ".flexlayout__tab #mapContainer": {
          bottom: 0.25,
        },
        ".flexlayout__float_window": {
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.3)",
        },
        ".flexlayout__float_window_header": {
          minHeight: 5,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        anchorOriginTopCenter: {
          top: "62px !important",
        },
      },
    },
    /**
     * Default props for all Mui Components
     */
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingBottom: 4,
        },
      },
    },
    MuiSlider: {
      defaultProps: {
        track: false,
        size: "small",
      },
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            "& .MuiSlider-thumb": {
              backgroundColor: grey[400],
            },
            "& .MuiSlider-track": {
              background: grey[400],
            },
            "& .MuiSlider-rail": {
              background: grey[400],
            },
          },
        },
        thumb: {
          backgroundColor: blue[500],
          border: `3px solid ${common.white}`,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          "&:hover": {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          },
          "&.Mui-focusVisible": {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          },
        },
        track: {
          opacity: 1,
          background: `linear-gradient(270deg, ${blue[500]} 1.83%, #E0E0E0 100%)`,
        },
        rail: {
          opacity: 0.3,
          background: blue[500],
        },
        markLabel: {
          fontSize: 12,
        },
        sizeSmall: {
          "& .MuiSlider-thumb": {
            height: 14,
            width: 14,
          },
          "& .MuiSlider-rail": {
            height: 3,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          "&.Mui-completed:not(.Mui-active)": {
            color: green[500],
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 50,
          height: 30,
          padding: 10,
          "& .MuiSwitch-thumb": {
            height: 20,
            width: 20,
          },
          "& .MuiSwitch-switchBase.Mui-checked": {
            transform: "translateX(20px)",
            ".MuiSwitch-thumb": {
              background: blue[500],
            },
            "+ .MuiSwitch-track": {
              opacity: 1,
              background: `linear-gradient(270deg, ${blue[500]} 15%, ${biarri.primary.lightGrey} 100%)`,
            },
            ":hover": {
              backgroundColor: "transparent",
            },
          },
          "& .MuiSwitch-switchBase.Mui-disabled": {
            ".MuiSwitch-thumb": {
              borderColor: "#F5F5F5",
            },
            "+ .MuiSwitch-track": {
              opacity: 0.4,
            },
          },
          "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
            ".MuiSwitch-thumb": {
              background: blue[200],
            },
          },
        },
        switchBase: {
          height: 30,
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
        thumb: {
          background: biarri.primary.lightGrey,
          border: `3px solid ${common.white}`,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
        },
        track: {
          opacity: 1,
          height: 12,
          background: `linear-gradient(90deg, #C0C0C0 1.83%, ${biarri.primary.lightGrey} 100%)`,
        },
      },
    },
  },
});

export default theme;
